"use client";

import { AlertTriangle, RefreshCw, Home, ArrowLeft } from "lucide-react";
import { useRouter } from "next/navigation";
import { Typography } from "@/components/common/ui/Typography";

export interface ErrorBoundaryProps {
  error: Error & { digest?: string };
  reset: () => void;
}

export function ErrorBoundary({ error, reset }: ErrorBoundaryProps) {
  const router = useRouter();

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-red-50 via-white to-orange-50 flex items-center justify-center p-6">
      {/* Animated Background Blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-red-200/30 rounded-full blur-3xl animate-blob" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-orange-200/30 rounded-full blur-3xl animate-blob animation-delay-2000" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-amber-200/20 rounded-full blur-3xl animate-blob animation-delay-4000" />
      </div>

      <div className="max-w-2xl w-full relative z-10">
        {/* Error Icon */}
        <div className="text-center mb-8">
          <div className="relative inline-block">
            {/* Pulsing background */}
            <div className="absolute inset-0 bg-red-500/20 rounded-full blur-2xl animate-pulse" />

            {/* Icon container */}
            <div className="relative bg-white rounded-full p-6 shadow-2xl border-4 border-red-100">
              <AlertTriangle
                className="w-16 h-16 text-red-500"
                strokeWidth={2}
              />
            </div>

            {/* Decorative elements */}
            <div className="absolute -top-2 -right-2 w-4 h-4 bg-red-400 rounded-full animate-ping" />
            <div className="absolute -bottom-2 -left-2 w-3 h-3 bg-orange-400 rounded-full animate-pulse" />
          </div>
        </div>

        {/* Error Content */}
        <div className="text-center mb-8">
          <Typography variant="display2" className="md:text-5xl mb-4">
            Oops! Something went wrong
          </Typography>

          <Typography variant="headline4" weight="normal" color="muted" as="p" className="mb-6">
            We encountered an unexpected error. Don't worry, our team has been
            notified.
          </Typography>

          {/* Error Details Card */}
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-red-100 shadow-lg mb-8">
            <div className="text-left">
              <Typography variant="body" weight="semibold" color="text-gray-700" as="p" className="mb-2">
                Error Details:
              </Typography>
              <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                <Typography variant="body" color="text-red-800" as="p" mono className="break-all">
                  {error.message || "An unexpected error occurred"}
                </Typography>
                {error.digest && (
                  <Typography variant="small" color="error" as="p" className="mt-2">
                    Error ID: {error.digest}
                  </Typography>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          {/* Try Again Button */}
          <button
            onClick={reset}
            className="group relative overflow-hidden bg-gradient-to-r from-navigator-primary to-navigator-primary-dark text-white rounded-2xl transition-all duration-300 shadow-lg hover:shadow-2xl w-full sm:w-auto"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000" />
            <div className="relative flex items-center justify-center gap-3 px-8 py-4">
              <RefreshCw className="w-5 h-5 group-hover:rotate-180 transition-transform duration-500" />
              <span className="font-semibold text-lg">Try Again</span>
            </div>
          </button>

          {/* Go Back Button */}
          <button
            onClick={() => router.back()}
            className="group bg-white hover:bg-gray-50 text-gray-700 rounded-2xl transition-all duration-300 shadow-md hover:shadow-xl border-2 border-gray-200 hover:border-gray-300 w-full sm:w-auto"
          >
            <div className="flex items-center justify-center gap-3 px-8 py-4">
              <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
              <span className="font-semibold text-lg">Go Back</span>
            </div>
          </button>

          {/* Home Button */}
          <button
            onClick={() => router.push("/home")}
            className="group bg-white hover:bg-gray-50 text-navigator-primary rounded-2xl transition-all duration-300 shadow-md hover:shadow-xl border-2 border-navigator-primary/20 hover:border-navigator-primary/40 w-full sm:w-auto"
          >
            <div className="flex items-center justify-center gap-3 px-8 py-4">
              <Home className="w-5 h-5" />
              <span className="font-semibold text-lg">Go Home</span>
            </div>
          </button>
        </div>

        {/* Help Text */}
        <div className="text-center mt-8">
          <div className="inline-flex items-center gap-2 px-6 py-3 bg-white/80 backdrop-blur-sm rounded-full shadow-md border border-gray-200">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            <Typography variant="body" color="muted" as="span">
              Need help? Contact our support team
            </Typography>
          </div>
        </div>
      </div>
    </div>
  );
}
